import {
  NetworkQualityLevelChangedPayload,
  RemoteParticipantConnectionEventPayload,
  SpeakingEventPayload,
  TrackReceivedEventPayload
} from '@app/models/types';
import {EventEmitter} from '@angular/core';
import {AccessType} from '@app/modules/shared/data/constants';
import {Observable} from 'rxjs';
import {RoomConnection} from '@app/modules/wrtc/models/room-connection';
import {DeviceMetadata} from '@app/modules/wrtc/models/device-metadata';
import {LocalVideoTrack} from 'twilio-video/tsdef/LocalVideoTrack';
import {LocalAudioTrack} from 'twilio-video/tsdef/LocalAudioTrack';

export abstract class WRTCProviderService {
  public onConnectedEventEmitter: EventEmitter<RoomConnection> = new EventEmitter<RoomConnection>();
  public onConnectionErrorEventEmitter: EventEmitter<RoomConnection> = new EventEmitter<RoomConnection>();

  public studentsIndividualEventEmittersById$: {
    [key: number]: {
      voiceMeterLevelChanged: EventEmitter<number>;
      networkLevelChanged: EventEmitter<NetworkQualityLevelChangedPayload>;
    };
  } = {};

  abstract get isConnected(): boolean;

  abstract connectToRoom(token: string, userId: number, roomName: string, role: AccessType): Observable<RoomConnection>;

  abstract disconnectFromRoom(): void;

  abstract reconnectToRoom(): Observable<RoomConnection>;

  abstract emmitVoiceLevel(mediaStreamSource: MediaStreamAudioSourceNode, analyser: AnalyserNode, stream: MediaStream, eventEmitter: EventEmitter<number>): void;

  abstract setCurrentUserMediaWrapper(wrapper: HTMLElement);

  abstract setCurrentUserVideoFrameRate(frameRate: number) ;

  abstract setCurrentUserVideoResolution(resolution: string) ;

  abstract addRemoteParticipantInfo(userId: number, accessType: AccessType, wrapper: HTMLElement);

  abstract removeRemoteParticipantMediaWrappers(id: number);

  abstract toggleDisableSelfAudio(isMuted: boolean);

  abstract toggleDisableSelfVideo(isMuted: boolean);

  abstract setSubscribeToAudioTrack(id: number, subscribe: boolean);

  abstract toggleSubscribeToVideoTrack(id: number, subscribe: boolean);

  abstract getParticipantConnectedEventEmitter(): EventEmitter<RemoteParticipantConnectionEventPayload>;

  abstract setParticipantVoiceLevel(userId: number, voiceLevel: number);

  abstract getAudioTrackReceivedEventEmitter(): EventEmitter<TrackReceivedEventPayload>;

  abstract getVideoTrackReceivedEventEmitter(): EventEmitter<TrackReceivedEventPayload>;

  /* fires on every real change of the local camera's availability, whatever caused it */
  abstract getLocalVideoProvidedChangedEventEmitter(): EventEmitter<boolean>;

  /* fires on every real change of the local microphone's availability, whatever caused it */
  abstract getLocalAudioProvidedChangedEventEmitter(): EventEmitter<boolean>;

  abstract getDominantSpeakerChangedEventEmitter(): EventEmitter<number>;

  abstract getVideoAndAudioDevices(): Promise<{
    audio: DeviceMetadata[];
    video: DeviceMetadata[];
    audioOutput: DeviceMetadata[];
  }>;

  abstract loadAndAttachVideoTrackById(deviceId: string, videoMediaWrapper: any): Observable<string>;

  abstract detachAndStopVideoTrack(deviceId: string, doDetach: boolean);

  abstract stopAudioTrack(deviceId: string);

  abstract getVideoDeviceById(trackInfo): Observable<LocalVideoTrack>;

  abstract getAudioDeviceById(trackInfo): Observable<LocalAudioTrack>;

  abstract updateLocalDevice(deviceId: string): void;

  abstract setSpeakerDevice(deviceId: string): void;

  abstract detachLocalParticipantVideo();

  abstract reattachLocalParticipantVideoTrack();

  abstract republishLocalParticipantVideoTrack(deviceId?: string): Promise<void>;

  abstract republishLocalParticipantAudioTrack(deviceId?: string): Promise<void>;

  abstract shareScreen(): void;

  abstract getVideoTrackByDeviceId(deviceId: string): Promise<LocalVideoTrack>;
  abstract getAudioTrackByDeviceId(deviceId: string): Promise<LocalAudioTrack>;

  abstract getLocalUserAudioTrack(role: AccessType, cancelNoise: boolean): Observable<LocalAudioTrack>;
  abstract getLocalUserVideoTrack(role: AccessType): Observable<LocalVideoTrack>;

  abstract onUserSpeak(userId: number): EventEmitter<SpeakingEventPayload>;
}
